/**
 * Notification toast components and the `addNotificationToast` imperative
 * trigger.
 *
 * `<RenderNotificationToasts>` positions the active toast stack in the
 * bottom-right corner and should be mounted once at the app root.
 * `addNotificationToast` is re-exported from `util/appdata` so callers have a
 * single import point rather than reaching into util directly. Toasts are
 * managed in global app state and dismissed via
 * `appData.removeNotificationToast`.
 */
import { CheckmarkFilled, CloseLarge, ErrorFilled } from '@carbon/icons-react';
import { useTheme } from '@repo/theme';
import { StyleSheet, View } from 'react-native';
import { addNotificationToast } from 'util/appdata';
import { useAppData, useNotificationToasts } from 'util/appdataContext';
import type { FormatParams, NotificationToast, TLabel } from 'util/stdtypes';
import { t } from 'util/stdtypes';

import { HorizBox, HoverView } from './basics';
import { UtilityText } from './text';

/** @public */
export { addNotificationToast };

interface NotificationToastProps {
    type: 'info' | 'success' | 'error';
    id: string;
    label: TLabel;
    formatParams?: FormatParams;
    dismissable?: boolean;
}

export function NotificationToast({ id, type, label, dismissable, formatParams }: NotificationToastProps) {
    const s = NotificationToastStyle;
    const appData = useAppData();
    const { colors } = useTheme();
    return (
        <View
            style={[
                s.container,
                { backgroundColor: colors.colorSurfacePopoverDark },
                type === 'error' && {
                    backgroundColor: colors.colorSurfaceBannerWarning,
                    borderWidth: 0.5,
                    borderColor: colors.colorBorderWarning,
                },
            ]}
        >
            <HorizBox gap={8}>
                {type === 'success' && (
                    <View style={s.iconContainer}>
                        <CheckmarkFilled color={colors.colorTextOnToastDark} size={20} />
                    </View>
                )}
                {type === 'error' && (
                    <View style={s.iconContainer}>
                        <ErrorFilled color={colors.colorTextWarning} size={20} />
                    </View>
                )}
                <UtilityText
                    color={type === 'error' ? colors.colorTextPrimary : colors.colorTextOnToastDark}
                    formatParams={formatParams}
                    label={label}
                    role='alert'
                    type='small'
                />
                {dismissable && (
                    <HoverView
                        ariaLabel={t('notification.dismiss_button', 'Dismiss')}
                        icon={CloseLarge}
                        onPress={() => {
                            appData.removeNotificationToast(id);
                        }}
                        role='button'
                        style={s.dismissButton}
                    >
                        <CloseLarge color={type === 'error' ? colors.colorTextPrimary : colors.colorTextOnToastDark} />
                    </HoverView>
                )}
            </HorizBox>
        </View>
    );
}

const NotificationToastStyle = StyleSheet.create({
    container: {
        padding: 20,
        borderRadius: 8,
        width: 335,
    },
    iconContainer: {
        flexShrink: 0,
    },
    dismissButton: {
        marginLeft: 'auto',
    },
});

export function RenderNotificationToasts() {
    const s = RenderNotificationToastStyle;
    const toasts = useNotificationToasts();
    if (toasts.length === 0) {
        return null;
    }
    return (
        <View style={s.container}>
            {toasts.map((toast) => (
                <NotificationToast key={toast.id} {...toast} />
            ))}
        </View>
    );
}

const RenderNotificationToastStyle = StyleSheet.create({
    container: {
        position: 'absolute',
        bottom: 50,
        right: 50,
        display: 'flex',
        flexDirection: 'column',
        gap: 12,
    },
});
